/**
 * Stone Investment Property List Card
 * 
 * List/horizontal variant of property card with Stone Investment's luxury aesthetic:
 * - Horizontal layout
 * - More detail visible
 * - HorizonOverlay effect
 */

'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState, memo } from 'react';
import { ArrowUpRight, Bed, Bath, Maximize, MapPin } from 'lucide-react';
import { WishlistButton } from '@/components/customer/WishlistButton';
import { FormatPrice } from '@/components/ui/FormatPrice';
import type { ListingCardData } from '@/components/listings/types';
import {
  getListingTypeLabel,
  getListingStatusLabel,
} from '@/components/listings/types';
import { detectRegion } from '../utils/regionColors';
import { HorizonOverlay } from './PropertyCard';

export interface StonePropertyListCardProps {
  /**
   * Listing data
   */
  listing: ListingCardData;
  /**
   * Link URL
   */
  href: string;
  /**
   * Show agent info
   */
  showAgent?: boolean;
  /**
   * Show status badge
   */
  showStatus?: boolean;
  /**
   * Show wishlist button
   */
  showWishlistButton?: boolean;
  /**
   * Current locale
   */
  locale?: string;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StonePropertyListCard - Horizontal property card
 */
export const StonePropertyListCard = memo(function StonePropertyListCard({
  listing,
  href,
  showAgent = false,
  showStatus = true,
  showWishlistButton = true,
  locale: _locale = 'en',
  messages = {},
  className = '',
}: StonePropertyListCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  const { property, location, listingType, status, price, listingAgent } = listing;
  
  const hasSpecialStatus = ['UNDER_OFFER', 'SOLD', 'LEASED', 'COMING_SOON'].includes(status);
  
  const locationString = [
    location?.suburb,
    location?.city,
    location?.district,
    location?.neighbourhood,
    location?.region,
  ].filter(Boolean).join(', ') || location?.name || (messages.locationTBA || 'Location TBA');

  const locationCountry = (location?.country || '').toLowerCase();
  const isFrance = locationCountry === 'france'
    || (!locationCountry && detectRegion(locationString) === 'provence');
  
  return (
    <Link href={href} className="block no-underline text-inherit">
      <article
        className={`group grid grid-cols-1 md:grid-cols-12 gap-6 py-6 border-b border-stone-200 hover:bg-stone-50/50 transition-colors ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image */}
        <div className="md:col-span-4 lg:col-span-3">
          <div className="relative aspect-[4/3] overflow-hidden bg-stone-200">
            {property.primaryImageUrl ? (
              <Image
                src={property.primaryImageUrl}
                alt={property.name}
                fill
                loading="lazy"
                className={`object-cover transition-transform duration-[2000ms] ease-out ${
                  isHovered ? 'scale-105' : 'scale-100'
                }`}
                sizes="(max-width: 768px) 100vw, 33vw"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-stone-300 to-stone-200 flex items-center justify-center">
                <span className="text-stone-400 font-sans text-sm">{messages.noImage || 'No Image'}</span>
              </div>
            )}
            
            {/* HorizonOverlay */}
            <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />
            
            {/* Wishlist Button */}
            {showWishlistButton && !hasSpecialStatus && (
              <div className="absolute top-3 left-3 z-20">
                <WishlistButton
                  entityType="PROPERTY"
                  entityId={property.id}
                  variant="solid"
                  size="sm"
                  className="bg-white/90 hover:bg-white shadow-sm"
                />
              </div>
            )}
            
            {/* Listing Type Badge */}
            <div className="absolute top-3 right-3 px-2 py-1 bg-stone-900 text-[9px] uppercase tracking-[0.15em] text-white font-sans">
              {getListingTypeLabel(listingType)}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="md:col-span-6 lg:col-span-7 flex flex-col justify-center">
          {/* Location */}
          <div className="flex items-center gap-2 text-stone-500 mb-2">
            <MapPin size={12} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.2em] font-sans">{locationString}</span>
          </div>
          
          {/* Title */}
          <h3 className={`font-serif text-2xl text-stone-900 mb-3 leading-tight transition-all duration-500 ${
            isHovered ? 'italic' : ''
          }`}>
            {listing.mainHeadline || property.name}
          </h3>
          
          {/* Description */}
          {property.description && (
            <p className="text-sm text-stone-600 font-sans line-clamp-2 mb-4">
              {property.description}
            </p>
          )}
          
          {/* Property Details */}
          <div className="flex flex-wrap items-center gap-4 text-stone-600 font-sans text-sm">
            {listing.propertyType && (
              <>
                <span className="text-stone-900 font-medium">{listing.propertyType.name}</span>
                <span className="w-px h-3 bg-stone-300" />
              </>
            )}
            
            {property.bedrooms !== null && property.bedrooms > 0 && (
              <span className="flex items-center gap-1.5">
                <Bed size={14} className="text-stone-400" />
                {property.bedrooms}
              </span>
            )}
            
            {property.bathrooms !== null && property.bathrooms > 0 && (
              <span className="flex items-center gap-1.5">
                <Bath size={14} className="text-stone-400" />
                {property.bathrooms}
              </span>
            )}
            
            {property.totalFloorArea !== null && property.totalFloorArea > 0 && (
              <span className="flex items-center gap-1.5">
                <Maximize size={14} className="text-stone-400" />
                {property.totalFloorArea.toLocaleString()} {property.totalFloorAreaUnit?.toLowerCase() || 'm²'}
              </span>
            )}
          </div>
          
          {/* Agent */}
          {showAgent && listingAgent && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-stone-200">
              {listingAgent.avatar ? (
                <Image
                  src={listingAgent.avatar}
                  alt={listingAgent.name}
                  width={28}
                  height={28}
                  className="object-cover object-top"
                />
              ) : (
                <div className="w-7 h-7 bg-stone-200 flex items-center justify-center">
                  <span className="text-stone-600 font-serif text-xs">{listingAgent.name.charAt(0)}</span>
                </div>
              )}
              <span className="text-sm text-stone-600 font-sans">{listingAgent.name}</span>
            </div>
          )}
        </div>
        
        {/* Price & Action */}
        <div className="md:col-span-2 flex flex-col justify-center items-start md:items-end">
          {/* Status Badge */}
          {showStatus && hasSpecialStatus && (
            <span className="px-2 py-1 bg-stone-900 text-[9px] uppercase tracking-[0.15em] text-white font-sans mb-3">
              {getListingStatusLabel(status)}
            </span>
          )}
          
          {/* Price */}
          {price ? (
            <FormatPrice
              amount={price.amount}
              currency={price.currency}
              frequency={price.frequency ?? null}
              size="lg"
              priceOnRequestText={messages.priceOnRequest || 'Price on request'}
              className="!text-stone-900 !font-serif"
            />
          ) : (
            <span className="font-serif text-xl text-stone-700 italic">{messages.priceOnRequest || 'Price on request'}</span>
          )}
          
          {/* Arrow */}
          <div className={`mt-4 transition-all duration-500 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 md:translate-x-2'}`}>
            <ArrowUpRight size={20} className="text-stone-900" strokeWidth={1} />
          </div>
        </div>
      </article>
    </Link>
  );
});

export default StonePropertyListCard;
